import React, { useState, useEffect, useCallback } from 'react';
import {
    Typography,
    Container,
    Grid,
    Card,
    CardContent,
    CardActions,
    Button,
    Chip,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Box,
    Avatar,
    Divider,
    Alert,
    LinearProgress,
    CircularProgress,
} from '@mui/material';
import {
    Edit,
    Visibility,
    GetApp,
    ArrowBack,
    AddCircle
} from '@mui/icons-material';
import { toast, ToastContainer } from 'react-toastify';
import Swal from 'sweetalert2'; // Added Swal for full-screen loading
import NavBar from '../../structure/NavBar';
import { NavBarTop } from '../../structure/NavBarTop';
import 'react-toastify/dist/ReactToastify.css';
import ApiCaller from '../../constants/ApiCaller';
import urls from '../../constants/apiurl';
import { PayoutButton } from './PayoutButton';
import { useDispatch, useSelector } from "react-redux";

import {
    updateTopHeader,
} from "../../../redux/features/common/commonSlice";


// --- Event Modal (Create/Edit) - MOVED OUTSIDE FOR STABILITY ---
const EventModal = ({ open, onClose, eventData, handleCreateEvent, handleUpdateEvent, submissionLoading }) => {

    const isEditMode = Boolean(eventData);

    const [eventName, setEventName] = useState('');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [poolAmount, setPoolAmount] = useState('');
    const [minInvitesRequired, setMinInvitesRequired] = useState('');
    const [topPercentage, setTopPercentage] = useState('');
    const [maxParticipants, setMaxParticipants] = useState('');
    const [errors, setErrors] = useState({});

    useEffect(() => {
        if (!open) return; // Only update state if modal is opening

        if (isEditMode) {
            setEventName(eventData.eventName || '');
            setStartDate(eventData.startDate || '');
            setEndDate(eventData.endDate || '');
            setPoolAmount(eventData.poolAmount || '');
            setMinInvitesRequired(eventData.minInvitesRequired || '');
            setTopPercentage(eventData.topPercentage || '');
            setMaxParticipants(eventData.maxParticipants || '');
        } else {
            setEventName('');
            setStartDate('');
            setEndDate('');
            setPoolAmount('');
            setMinInvitesRequired('');
            setTopPercentage('');
            setMaxParticipants('');
        }
        setErrors({});
    }, [eventData, isEditMode, open]);

    function validate() {
        const err = {};
        if (!eventName.trim()) err.eventName = 'Event name is required';
        if (!startDate) err.startDate = 'Start date is required';
        if (!endDate) err.endDate = 'End date is required';
        if (startDate && endDate && new Date(endDate) < new Date(startDate))
            err.endDate = 'End date must be after start date';
        if (!poolAmount || Number(poolAmount) <= 0)
            err.poolAmount = 'Prize must be a positive number';
        if (!minInvitesRequired || Number(minInvitesRequired) <= 0)
            err.minInvitesRequired = 'Min invites must be a positive number';

        if (!topPercentage || Number(topPercentage) <= 0 || Number(topPercentage) > 100)
            err.topPercentage = 'Percentage must be between 1 and 100';
        if (!maxParticipants || Number(maxParticipants) <= 0)
            err.maxParticipants = 'Max participants must be a positive number';

        setErrors(err);
        return Object.keys(err).length === 0;
    }

    function handleSubmit() {
        if (!validate() || submissionLoading) return;

        const payload = {
            uuid: eventData?.uuid || '',
            eventName,
            startDate,
            endDate,
            poolAmount: Number(poolAmount),
            minInvitesRequired: Number(minInvitesRequired),
            topPercentage: Number(topPercentage),
            maxParticipants: Number(maxParticipants),
        };

        if (isEditMode) {
            handleUpdateEvent({ ...payload, recordId: eventData.recordId });
        } else {
            handleCreateEvent({ ...payload, eventStatus: "Pending" });
        }
    }

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>{isEditMode ? 'Edit Event' : 'Create New Event'}</DialogTitle>
            <DialogContent>
                <TextField
                    fullWidth label="Event Name" value={eventName}
                    onChange={e => setEventName(e.target.value)}
                    margin="normal" error={!!errors.eventName} helperText={errors.eventName}
                    autoFocus
                />
                <Box display="flex" gap={2} my={1}>
                    <TextField
                        type="date" label="Start Date" value={startDate}
                        onChange={e => setStartDate(e.target.value)}
                        sx={{ flex: 1 }} InputLabelProps={{ shrink: true }}
                        error={!!errors.startDate} helperText={errors.startDate}
                    />
                    <TextField
                        type="date" label="End Date" value={endDate}
                        onChange={e => setEndDate(e.target.value)}
                        sx={{ flex: 1 }} InputLabelProps={{ shrink: true }}
                        error={!!errors.endDate} helperText={errors.endDate}
                    />
                </Box>
                <TextField
                    fullWidth label="Prize Pool (₹)" type="number" value={poolAmount}
                    onChange={e => setPoolAmount(e.target.value)}
                    margin="normal" error={!!errors.poolAmount} helperText={errors.poolAmount}
                />
                <TextField
                    fullWidth label="Minimum Invites Required" type="number" value={minInvitesRequired}
                    onChange={e => setMinInvitesRequired(e.target.value)}
                    margin="normal" error={!!errors.minInvitesRequired} helperText={errors.minInvitesRequired}
                />
                <TextField
                    fullWidth label="Top Percentage (%)" type="number" value={topPercentage}
                    onChange={e => setTopPercentage(e.target.value)}
                    margin="normal" error={!!errors.topPercentage} helperText={errors.topPercentage}
                />
                <TextField
                    fullWidth label="Max Participants" type="number" value={maxParticipants}
                    onChange={e => setMaxParticipants(e.target.value)}
                    margin="normal" error={!!errors.maxParticipants} helperText={errors.maxParticipants}
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={submissionLoading}>Cancel</Button>
                <Button
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={submissionLoading}
                >
                    {submissionLoading ? 'Processing...' : (isEditMode ? 'Update Event' : 'Create Event')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};


const Leaderboard = () => {
    const [currentView, setCurrentView] = useState('dashboard');
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [events, setEvents] = useState([]);
    const [leaderboard, setLeaderboard] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // --- State for the new combined modal ---
    const [eventModalOpen, setEventModalOpen] = useState(false);
    const [editingEvent, setEditingEvent] = useState(null);    // null = Create mode, object = Edit mode

    const dispatch = useDispatch();
    const userData = useSelector(state => state?.userData)


    // --- API handler for UPDATING an event ---
    async function handleUpdateEvent(payload) {
        setLoading(true);
        // Show full-screen loader
        Swal.fire({
            title: 'Updating Event...',
            allowOutsideClick: false,
            didOpen: () => {
                Swal.showLoading();
            }
        });

        try {
            let path = urls.updateTopInviterEvent;
            let postJson = payload;

            const res = await ApiCaller.post(postJson, path, userData?.access_token);
            const result = await res.response.json();

            if (result.status !== '200' && result.status !== 200)
                throw new Error(result.message || 'Failed to update event.');

            // Success: Close Modal and Swal IMMEDIATELY
            setEventModalOpen(false);
            setEditingEvent(null);
            Swal.close(); 
            
            toast.success(result.message || 'Event updated successfully!');
            
            // Background refresh (non-blocking for UI closure)
            fetchEvents();
        } catch (err) {
            console.error("Error updating event:", err);
            toast.error(err.message);
            Swal.close();
        } finally {
            setLoading(false);
        }
    }


    // --- Fetch events safely ---
    const fetchEvents = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            let path = urls.topInviterEventList;
            let postJson = {};

            const res = await ApiCaller.post(postJson, path, userData?.access_token);

            const apiResponse = await res.response.json();

            // Ensure participants array is always non-null (even if empty from backend now)
            const safeEvents = (apiResponse.data || []).map(ev => ({
                ...ev,
                participants: ev.participants || [],
            }));

            setEvents(safeEvents);
        } catch (err) {
            toast.error(err.message);
            setError(err.message);
            setEvents([]);
        } finally {
            setLoading(false);
        }
    }, [userData]);

    useEffect(() => {
        fetchEvents();
    }, [fetchEvents]);

    useEffect(() => {
        dispatch(updateTopHeader("Top Inviter Events"));
    }, [dispatch]);

    // --- Helpers ---
    function formatCurrency(amountInPaise) {
        if (typeof amountInPaise !== 'number') return '-';
        return `₹${(amountInPaise / 100).toLocaleString('en-IN', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        })}`;
    }

    function getStatusColor(status) {
        const map = {
            RUNNING: 'success',
            PAUSED: 'warning',
            COMPLETED: 'default',
            DRAFT: 'primary',
        };
        return map[status] || 'default';
    }

    // --- UPDATED: Event click handler (Fetches data from API) ---
    async function handleEventClick(eventData) {
        setSelectedEvent(eventData);
        setCurrentView('detail');
        setLoading(true);
        setLeaderboard([]);

        try {
            // Updated to use the constant from apiurl.js as requested by user
            let path = urls.topInviterEventParticipantsList || '/api/admin-service/top-inviter-event/participants-list';
            
            // The participants-list endpoint might expect recordId in the request body
            const payload = { recordId: eventData.recordId };
            const res = await ApiCaller.post(payload, path, userData?.access_token);

            if (res && (res.status === 200 || res.status === '200')) {
                const apiResponse = await res.response.json();
                const participants = apiResponse.data || [];

                const leaderboardData = participants.map((p, index) => ({
                    rank: index + 1,
                    inviterId: p.userId,
                    inviterName: p.username,
                    inviteCount: p.invitedCount,
                    projectedPayout: (p.rewardAmount || 0) * 100,
                    kycStatus: 'verified',
                    flagged: !p.eligible,
                    recentInvitees: [],
                }));

                setLeaderboard(leaderboardData);
            } else {
                // If the participants-list (POST) fails, fallback to the record-specific GET as a safety measure
                let fallbackPath = `/api/admin-service/top-inviter-event/${eventData.recordId}/participants`;
                const fallbackRes = await ApiCaller.get({}, fallbackPath, userData?.access_token);
                
                if (fallbackRes && (fallbackRes.status === 200 || fallbackRes.status === '200')) {
                    const fallbackData = await fallbackRes.response.json();
                    setLeaderboard((fallbackData.data || []).map((p, index) => ({
                        rank: index + 1,
                        inviterId: p.userId,
                        inviterName: p.username,
                        inviteCount: p.invitedCount,
                        projectedPayout: (p.rewardAmount || 0) * 100,
                        kycStatus: 'verified',
                        flagged: !p.eligible,
                        recentInvitees: [],
                    })));
                } else {
                    toast.error("Failed to load participants");
                }
            }
        } catch (err) {
            console.error(err);
            toast.error("Error loading event details");
        } finally {
            setLoading(false);
        }
    }

    // --- Create Event Handler ---
    async function handleCreateEvent(payload) {
        setLoading(true);
        // Show full-screen loader
        Swal.fire({
            title: 'Creating Event...',
            allowOutsideClick: false,
            didOpen: () => {
                Swal.showLoading();
            }
        });

        try {
            let path = urls.createTopInviterEvent;
            let postJson = payload;
            const res = await ApiCaller.post(postJson, path, userData?.access_token);
            const result = await res.response.json();

            if (result.status !== '200' && result.status !== 200)
                throw new Error(result.message || 'Failed to create event.');

            // Success: Close Modal and Swal IMMEDIATELY
            setEventModalOpen(false);
            setEditingEvent(null);
            Swal.close();

            toast.success(result.message || 'Event created successfully!');
            
            // Background refresh (non-blocking for UI closure)
            fetchEvents();
        } catch (err) {
            console.error(err);
            toast.error(err.message);
            setError(err.message);
            Swal.close();
        } finally {
            setLoading(false);
        }
    }

    // --- UPDATED: EventCard uses 'totalInvitedUsers' ---
    const EventCard = ({ event }) => (
        <Card
            sx={{
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                '&:hover': { transform: 'translateY(-4px)', boxShadow: 3 },
            }}
            onClick={() => handleEventClick(event)}
        >
            <CardContent sx={{ pb: 1 }}>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                    <Typography variant="h6" component="h3" fontWeight={600}>
                        {event.eventName}
                    </Typography>
                    <Chip
                        label={(event.eventStatus || 'DRAFT').toUpperCase()}
                        color={getStatusColor(event.eventStatus)}
                        size="small"
                    />
                </Box>

                <Typography variant="body2" color="text.secondary" mb={2}>
                    {event.startDate} to {event.endDate}
                </Typography>

                <Grid container spacing={2} mb={2}>
                    <Grid item xs={6}>
                        <Box textAlign="center" p={1} bgcolor="grey.50" borderRadius={1}>
                            <Typography variant="h5" fontWeight={700} color="success.main">
                                {formatCurrency(event.poolAmount * 100)}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" textTransform="uppercase">
                                Prize Pool
                            </Typography>
                        </Box>
                    </Grid>
                    <Grid item xs={6}>
                        <Box textAlign="center" p={1} bgcolor="grey.50" borderRadius={1}>
                            <Typography variant="h6" fontWeight={700}>
                                {event.maxParticipants || 0}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" textTransform="uppercase">
                                Winners
                            </Typography>
                        </Box>
                    </Grid>
                </Grid>

                <Typography variant="caption" color="text.secondary">
                    {event.totalInvitedUsers || 0} invited count
                </Typography>
            </CardContent>

            <CardActions>
                <Button 
                    size="small" 
                    startIcon={<Visibility />}
                    onClick={(e) => {
                        e.stopPropagation(); 
                        handleEventClick(event);
                    }}
                >
                    View
                </Button>
                <Button
                    size="small"
                    startIcon={<Edit />}
                    onClick={e => {
                        e.stopPropagation();
                        setEditingEvent(event);
                        setEventModalOpen(true);
                    }}
                >
                    Edit
                </Button>
                <Button
                    size="small"
                    startIcon={<GetApp />}
                    onClick={e => {
                        e.stopPropagation();
                        toast.info("Export not implemented.");
                    }}
                >
                    Export
                </Button>
            </CardActions>
        </Card>
    );

    const LeaderboardRow = ({ inviter, rank }) => (
        <TableRow hover sx={{ cursor: 'pointer' }}>
            <TableCell>{rank}</TableCell>
            <TableCell>
                <Box display="flex" alignItems="center" gap={1} >
                    <Avatar sx={{ width: 32, height: 32, fontSize: '0.875rem' }}>
                        {inviter.inviterName?.charAt(0) || 'U'}
                    </Avatar>
                    <Box>
                        <Typography variant="body2" fontWeight={500}>
                            {inviter.inviterName || 'Unknown'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            {inviter.inviterId}
                        </Typography>
                    </Box>
                </Box>
            </TableCell>
            <TableCell align="right">{inviter.inviteCount || 0}</TableCell>
            <TableCell align="right" sx={{ fontWeight: 600, color: 'success.main' }}>
                {formatCurrency(inviter.projectedPayout)}
            </TableCell>
        </TableRow>
    );




    return (
        <>
            <NavBar />
            <div className="main-content" >
                <NavBarTop />
                <Box component="main" sx={{ flexGrow: 1, py: 5, px: 3, }}>
                    <div style={{ padding: '10px' }}>
                        {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}
                        {loading && <LinearProgress />}

                        {currentView === 'dashboard' && (
                            <>
                                <div style={{ marginTop: "20px", paddingTop: "20px", display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: '#f5f5f5', padding: '10px 20px', borderRadius: '8px' }}>
                                    <Typography variant="h5" fontWeight={400}>
                                        Top Inviter Events List
                                    </Typography>

                                    <Button
                                        variant="contained"
                                        color="primary"
                                        startIcon={<AddCircle />}
                                        onClick={() => {
                                            setEditingEvent(null);
                                            setEventModalOpen(true);
                                        }}
                                        sx={{ fontSize: '0.875rem' }}
                                    >
                                        Create Event
                                    </Button>
                                </div>
                                <Grid container spacing={3} sx={{ padding: 3, }}>
                                    {events.map(ev => (
                                        <Grid item xs={12} sm={6} md={4} key={ev.recordId}>
                                            <EventCard event={ev} />
                                        </Grid>
                                    ))}
                                </Grid>
                            </>
                        )}

                        {currentView === 'detail' && selectedEvent && (
                            <Grid container spacing={3}>
                                <Grid item xs={12} md={12}>
                                    <Paper sx={{ p: 2 }}>
                                        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                                            <Typography variant="h5" fontWeight={700}>
                                                {selectedEvent.eventName}
                                            </Typography>
                                            <Box display="flex" gap={1} alignItems="center">
                                                <Button
                                                    startIcon={<ArrowBack />}
                                                    onClick={() => {
                                                        setCurrentView('dashboard');
                                                        setSelectedEvent(null);
                                                    }}
                                                >
                                                    Back
                                                </Button>

                                                <PayoutButton
                                                    eventId={selectedEvent?.recordId}
                                                    disabled={String(selectedEvent?.eventStatus).toUpperCase() === 'COMPLETED'}
                                                >
                                                    {String(selectedEvent?.eventStatus).toUpperCase() === 'COMPLETED'
                                                        ? 'Payout Already Done'
                                                        : 'Payout'}
                                                </PayoutButton>

                                            </Box>
                                        </Box>
                                        <Divider sx={{ mb: 2 }} />
                                        <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: '60vh' }}>
                                            <Table stickyHeader size="small">
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell>Rank</TableCell>
                                                        <TableCell>Inviter</TableCell>
                                                        <TableCell align="right">Invites</TableCell>
                                                        <TableCell align="right">Reward Amount</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {loading ? (
                                                        <TableRow>
                                                            <TableCell colSpan={4} align="center" sx={{ py: 3 }}>
                                                                <CircularProgress size={24} sx={{ mr: 2 }} />
                                                                <Typography variant="body2" component="span" color="text.secondary">
                                                                    Loading participants...
                                                                </Typography>
                                                            </TableCell>
                                                        </TableRow>
                                                    ) : leaderboard.length > 0 ? (
                                                        leaderboard.map((inv, idx) => (
                                                            <LeaderboardRow key={inv.inviterId} inviter={inv} rank={inv.rank || idx + 1} />
                                                        ))
                                                    ) : (
                                                        <TableRow>
                                                            <TableCell colSpan={4} align="center" sx={{ py: 3 }}>
                                                                <Typography variant="body2" color="text.secondary">
                                                                    No participants found for this event.
                                                                </Typography>
                                                            </TableCell>
                                                        </TableRow>
                                                    )}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    </Paper>
                                </Grid>
                            </Grid>
                        )}

                        <EventModal
                            open={eventModalOpen}
                            onClose={() => {
                                setEventModalOpen(false);
                                setEditingEvent(null);
                            }}
                            eventData={editingEvent}
                            handleCreateEvent={handleCreateEvent}
                            handleUpdateEvent={handleUpdateEvent}
                            submissionLoading={loading}
                        />

                        <ToastContainer position="top-right" autoClose={3000} />
                    </div>
                </Box>
            </div>
        </>
    );
};

export default Leaderboard;


// import React, { useState, useEffect, useCallback } from 'react';
// import {
//     Typography,
//     Container,
//     Grid,
//     Card,
//     CardContent,
//     CardActions,
//     Button,
//     Chip,
//     Table,
//     TableBody,
//     TableCell,
//     TableContainer,
//     TableHead,
//     TableRow,
//     Paper,
//     Dialog,
//     DialogTitle,
//     DialogContent,
//     DialogActions,
//     TextField,
//     Box,
//     Avatar,
//     Divider,
//     Alert,
//     LinearProgress,
// } from '@mui/material';
// import {
//     Edit,
//     Visibility,
//     GetApp,
//     ArrowBack,
//     AddCircle
// } from '@mui/icons-material';
// import { toast, ToastContainer } from 'react-toastify';
// import NavBar from '../../structure/NavBar';
// import { NavBarTop } from '../../structure/NavBarTop';
// import 'react-toastify/dist/ReactToastify.css';
// import ApiCaller from '../../constants/ApiCaller';
// import urls from '../../constants/apiurl';
// import { PayoutButton } from './PayoutButton';
// import { useDispatch, useSelector } from "react-redux";

// import {
//     updateTopHeader,
// } from "../../../redux/features/common/commonSlice";


// const Leaderboard = () => {
//     const [currentView, setCurrentView] = useState('dashboard');
//     const [selectedEvent, setSelectedEvent] = useState(null);
//     const [events, setEvents] = useState([]);
//     const [leaderboard, setLeaderboard] = useState([]);
//     const [loading, setLoading] = useState(true);
//     const [error, setError] = useState(null);

//     // --- State for the new combined modal ---
//     const [eventModalOpen, setEventModalOpen] = useState(false);
//     const [editingEvent, setEditingEvent] = useState(null);    // null = Create mode, object = Edit mode

//     const dispatch = useDispatch();
//     const userData = useSelector(state => state?.userData)


//     // --- API handler for UPDATING an event ---
//     async function handleUpdateEvent(payload) {
//         setLoading(true);
//         try {
//             let path = urls.updateTopInviterEvent;
//             let postJson = payload;

//             const res = await ApiCaller.post(postJson, path, userData?.access_token);
//             const result = await res.response.json();

//             if (result.status !== '200' && result.status !== 200)
//                 throw new Error(result.message || 'Failed to update event.');

//             toast.success(result.message || 'Event updated successfully!');
            
//             // Close modal and clear editing state BEFORE fetching new data
//             setEventModalOpen(false);
//             setEditingEvent(null);
            
//             await fetchEvents();      // Refresh list to show new data
//         } catch (err) {
//             console.error("Error updating event:", err);
//             toast.error(err.message);
//         } finally {
//             setLoading(false);
//         }
//     }


//     // --- Fetch events safely ---
//     const fetchEvents = useCallback(async () => {
//         setLoading(true);
//         setError(null);
//         try {
//             let path = urls.topInviterEventList;
//             let postJson = {};

//             const res = await ApiCaller.post(postJson, path, userData?.access_token);
//             const apiResponse = await res.response.json();

//             // Ensure participants array is always non-null (even if empty from backend now)
//             const safeEvents = (apiResponse.data || []).map(ev => ({
//                 ...ev,
//                 participants: ev.participants || [],
//             }));

//             setEvents(safeEvents);
//         } catch (err) {
//             toast.error(err.message);
//             setError(err.message);
//             setEvents([]);
//         } finally {
//             setLoading(false);
//         }
//     }, [userData]);

//     useEffect(() => {
//         fetchEvents();
//     }, [fetchEvents]);

//     useEffect(() => {
//         dispatch(updateTopHeader("Top Inviter Events"));
//     }, [dispatch]);

//     // --- Helpers ---
//     function formatCurrency(amountInPaise) {
//         if (typeof amountInPaise !== 'number') return '-';
//         return `₹${(amountInPaise / 100).toLocaleString('en-IN', {
//             minimumFractionDigits: 2,
//             maximumFractionDigits: 2,
//         })}`;
//     }

//     function getStatusColor(status) {
//         const map = {
//             RUNNING: 'success',
//             PAUSED: 'warning',
//             COMPLETED: 'default',
//             DRAFT: 'primary',
//         };
//         return map[status] || 'default';
//     }

//     // --- UPDATED: Event click handler (Fetches data from API) ---
//     async function handleEventClick(eventData) {
//         setSelectedEvent(eventData);
//         setCurrentView('detail');
//         setLoading(true);
//         setLeaderboard([]);

//         try {
//             // Fix: Ensure path starts with '/' to match Postman behavior if needed
//             let path = `top-inviter-event/${eventData.recordId}/participants`;
            
//             // Fix: Pass {} as first arg so ApiCaller treats 'path' as path, not params
//             const res = await ApiCaller.get({}, path, userData?.access_token);

//             if (res && (res.status === 200 || res.status === '200')) {
//                 const apiResponse = await res.response.json();
//                 const participants = apiResponse.data || [];

//                 const leaderboardData = participants.map((p, index) => ({
//                     rank: index + 1,
//                     inviterId: p.userId,
//                     inviterName: p.username,
//                     inviteCount: p.invitedCount,
//                     projectedPayout: (p.rewardAmount || 0) * 100,
//                     kycStatus: 'verified',
//                     flagged: !p.eligible,
//                     recentInvitees: [],
//                 }));

//                 setLeaderboard(leaderboardData);
//             } else {
//                 toast.error("Failed to load participants");
//             }
//         } catch (err) {
//             console.error(err);
//             toast.error("Error loading event details");
//         } finally {
//             setLoading(false);
//         }
//     }

//     // --- Create Event Handler ---
//     async function handleCreateEvent(payload) {
//         setLoading(true);
//         try {
//             let path = urls.createTopInviterEvent;
//             let postJson = payload;
//             const res = await ApiCaller.post(postJson, path, userData?.access_token);
//             const result = await res.response.json();

//             if (result.status !== '200' && result.status !== 200)
//                 throw new Error(result.message || 'Failed to create event.');

//             toast.success(result.message || 'Event created successfully!');
//             setEventModalOpen(false);
//             await fetchEvents();
//         } catch (err) {
//             console.error(err);
//             toast.error(err.message);
//             setError(err.message);
//         } finally {
//             setLoading(false);
//         }
//     }

//     // --- UPDATED: EventCard uses 'totalInvitedUsers' ---
//     const EventCard = ({ event }) => (
//         <Card
//             sx={{
//                 cursor: 'pointer',
//                 transition: 'all 0.2s ease',
//                 '&:hover': { transform: 'translateY(-4px)', boxShadow: 3 },
//             }}
//             onClick={() => handleEventClick(event)}
//         >
//             <CardContent sx={{ pb: 1 }}>
//                 <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
//                     <Typography variant="h6" component="h3" fontWeight={600}>
//                         {event.eventName}
//                     </Typography>
//                     <Chip
//                         label={(event.eventStatus || 'DRAFT').toUpperCase()}
//                         color={getStatusColor(event.eventStatus)}
//                         size="small"
//                     />
//                 </Box>

//                 <Typography variant="body2" color="text.secondary" mb={2}>
//                     {event.startDate} to {event.endDate}
//                 </Typography>

//                 <Grid container spacing={2} mb={2}>
//                     <Grid item xs={6}>
//                         <Box textAlign="center" p={1} bgcolor="grey.50" borderRadius={1}>
//                             <Typography variant="h5" fontWeight={700} color="success.main">
//                                 {formatCurrency(event.poolAmount * 100)}
//                             </Typography>
//                             <Typography variant="caption" color="text.secondary" textTransform="uppercase">
//                                 Prize Pool
//                             </Typography>
//                         </Box>
//                     </Grid>
//                     <Grid item xs={6}>
//                         <Box textAlign="center" p={1} bgcolor="grey.50" borderRadius={1}>
//                             {/* UPDATED: Using the count from the backend Map */}
//                             <Typography variant="h6" fontWeight={700}>
//                                 {event.totalInvitedUsers || 0}
//                             </Typography>
//                             <Typography variant="caption" color="text.secondary" textTransform="uppercase">
//                                 Winners
//                             </Typography>
//                         </Box>
//                     </Grid>
//                 </Grid>

//                 <Typography variant="caption" color="text.secondary">
//                     {event.totalInvitedUsers || 0} eligible winners
//                 </Typography>
//             </CardContent>

//             <CardActions>
//                 <Button 
//                     size="small" 
//                     startIcon={<Visibility />}
//                     onClick={(e) => {
//                         e.stopPropagation(); 
//                         handleEventClick(event);
//                     }}
//                 >
//                     View
//                 </Button>
//                 <Button
//                     size="small"
//                     startIcon={<Edit />}
//                     onClick={e => {
//                         e.stopPropagation();
//                         // --- Fix 3: Disable Edit if Completed ---
//                         if (String(event.eventStatus).toUpperCase() === 'COMPLETED') {
//                             toast.warning("Cannot edit a completed event.");
//                             return;
//                         }
//                         setEditingEvent(event);
//                         setEventModalOpen(true);
//                     }}
//                     // Optional: Visually disable the button too
//                     disabled={String(event.eventStatus).toUpperCase() === 'COMPLETED'}
//                 >
//                     Edit
//                 </Button>
//                 <Button
//                     size="small"
//                     startIcon={<GetApp />}
//                     onClick={e => {
//                         e.stopPropagation();
//                         toast.info("Export not implemented.");
//                     }}
//                 >
//                     Export
//                 </Button>
//             </CardActions>
//         </Card>
//     );

//     const LeaderboardRow = ({ inviter, rank }) => (
//         <TableRow hover sx={{ cursor: 'pointer' }}>
//             <TableCell>{rank}</TableCell>
//             <TableCell>
//                 <Box display="flex" alignItems="center" gap={1} >
//                     <Avatar sx={{ width: 32, height: 32, fontSize: '0.875rem' }}>
//                         {inviter.inviterName?.charAt(0) || 'U'}
//                     </Avatar>
//                     <Box>
//                         <Typography variant="body2" fontWeight={500}>
//                             {inviter.inviterName || 'Unknown'}
//                         </Typography>
//                         <Typography variant="caption" color="text.secondary">
//                             {inviter.inviterId}
//                         </Typography>
//                     </Box>
//                 </Box>
//             </TableCell>
//             <TableCell align="right">{inviter.inviteCount || 0}</TableCell>
//             <TableCell align="right" sx={{ fontWeight: 600, color: 'success.main' }}>
//                 {formatCurrency(inviter.projectedPayout)}
//             </TableCell>
//         </TableRow>
//     );


//     // --- Event Modal (Create/Edit) ---
//     const EventModal = ({ open, onClose, eventData }) => {

//         const isEditMode = Boolean(eventData);

//         const [eventName, setEventName] = useState('');
//         const [startDate, setStartDate] = useState('');
//         const [endDate, setEndDate] = useState('');
//         const [poolAmount, setPoolAmount] = useState('');
//         const [minInvitesRequired, setMinInvitesRequired] = useState('');
//         const [topPercentage, setTopPercentage] = useState('');
//         const [maxParticipants, setMaxParticipants] = useState('');
//         const [errors, setErrors] = useState({});

//         useEffect(() => {
//             // Clear errors whenever the modal opens or mode changes
//             setErrors({});
            
//             if (open && isEditMode && eventData) {
//                 // Populate form ONLY if open and in edit mode
//                 setEventName(eventData.eventName || '');
//                 setStartDate(eventData.startDate || '');
//                 setEndDate(eventData.endDate || '');
//                 setPoolAmount(eventData.poolAmount || '');
//                 setMinInvitesRequired(eventData.minInvitesRequired || '');
//                 setTopPercentage(eventData.topPercentage || '');
//                 setMaxParticipants(eventData.maxParticipants || '');
//             } else if (open && !isEditMode) {
//                 // Reset form for "Create New"
//                 setEventName('');
//                 setStartDate('');
//                 setEndDate('');
//                 setPoolAmount('');
//                 setMinInvitesRequired('');
//                 setTopPercentage('');
//                 setMaxParticipants('');
//             }
//         }, [eventData, isEditMode, open]); // Depend on 'open' to reset correctly

//         function validate() {
//             const err = {};
//             if (!eventName.trim()) err.eventName = 'Event name is required';
//             if (!startDate) err.startDate = 'Start date is required';
//             if (!endDate) err.endDate = 'End date is required';
//             if (startDate && endDate && new Date(endDate) < new Date(startDate))
//                 err.endDate = 'End date must be after start date';
//             if (!poolAmount || Number(poolAmount) <= 0)
//                 err.poolAmount = 'Prize must be a positive number';
//             if (!minInvitesRequired || Number(minInvitesRequired) <= 0)
//                 err.minInvitesRequired = 'Min invites must be a positive number';

//             if (!topPercentage || Number(topPercentage) <= 0 || Number(topPercentage) > 100)
//                 err.topPercentage = 'Percentage must be between 1 and 100';
//             if (!maxParticipants || Number(maxParticipants) <= 0)
//                 err.maxParticipants = 'Max participants must be a positive number';

//             setErrors(err);
//             return Object.keys(err).length === 0;
//         }

//         function handleSubmit() {
//             if (!validate()) return;

//             const payload = {
//                 uuid: eventData?.uuid || '',
//                 eventName,
//                 startDate,
//                 endDate,
//                 poolAmount: Number(poolAmount),
//                 minInvitesRequired: Number(minInvitesRequired),
//                 topPercentage: Number(topPercentage),
//                 maxParticipants: Number(maxParticipants),
//             };

//             if (isEditMode) {
//                 handleUpdateEvent({ ...payload, recordId: eventData.recordId });
//             } else {
//                 handleCreateEvent({ ...payload, eventStatus: "Pending" });
//             }
//         }

//         return (
//             <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
//                 <DialogTitle>{isEditMode ? 'Edit Event' : 'Create New Event'}</DialogTitle>
//                 <DialogContent>
//                     <TextField
//                         fullWidth label="Event Name" value={eventName}
//                         onChange={e => setEventName(e.target.value)}
//                         margin="normal" error={!!errors.eventName} helperText={errors.eventName}
//                         autoFocus
//                     />
//                     <Box display="flex" gap={2} my={1}>
//                         <TextField
//                             type="date" label="Start Date" value={startDate}
//                             onChange={e => setStartDate(e.target.value)}
//                             sx={{ flex: 1 }} InputLabelProps={{ shrink: true }}
//                             error={!!errors.startDate} helperText={errors.startDate}
//                         />
//                         <TextField
//                             type="date" label="End Date" value={endDate}
//                             onChange={e => setEndDate(e.target.value)}
//                             sx={{ flex: 1 }} InputLabelProps={{ shrink: true }}
//                             error={!!errors.endDate} helperText={errors.endDate}
//                         />
//                     </Box>
//                     <TextField
//                         fullWidth label="Prize Pool (₹)" type="number" value={poolAmount}
//                         onChange={e => setPoolAmount(e.target.value)}
//                         margin="normal" error={!!errors.poolAmount} helperText={errors.poolAmount}
//                     />
//                     <TextField
//                         fullWidth label="Minimum Invites Required" type="number" value={minInvitesRequired}
//                         onChange={e => setMinInvitesRequired(e.target.value)}
//                         margin="normal" error={!!errors.minInvitesRequired} helperText={errors.minInvitesRequired}
//                     />
//                     <TextField
//                         fullWidth label="Top Percentage (%)" type="number" value={topPercentage}
//                         onChange={e => setTopPercentage(e.target.value)}
//                         margin="normal" error={!!errors.topPercentage} helperText={errors.topPercentage}
//                     />
//                     <TextField
//                         fullWidth label="Max Participants" type="number" value={maxParticipants}
//                         onChange={e => setMaxParticipants(e.target.value)}
//                         margin="normal" error={!!errors.maxParticipants} helperText={errors.maxParticipants}
//                     />
//                 </DialogContent>
//                 <DialogActions>
//                     <Button onClick={onClose}>Cancel</Button>
//                     <Button variant="contained" onClick={handleSubmit}>
//                         {isEditMode ? 'Update Event' : 'Create Event'}
//                     </Button>
//                 </DialogActions>
//             </Dialog>
//         );
//     };


//     return (
//         <>
//             <NavBar />
//             <div className="main-content" >
//                 <NavBarTop />
//                 <Box component="main" sx={{ flexGrow: 1, py: 5, px: 3, }}>
//                     <div style={{ padding: '10px' }}>
//                         {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}
//                         {loading && <LinearProgress />}

//                         {currentView === 'dashboard' && (
//                             <>
//                                 <div style={{ marginTop: "20px", paddingTop: "20px", display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: '#f5f5f5', padding: '10px 20px', borderRadius: '8px' }}>
//                                     <Typography variant="h5" fontWeight={400}>
//                                         Top Inviter Events List
//                                     </Typography>

//                                     <Button
//                                         variant="contained"
//                                         color="primary"
//                                         startIcon={<AddCircle />}
//                                         onClick={() => {
//                                             setEditingEvent(null);
//                                             setEventModalOpen(true);
//                                         }}
//                                         sx={{ fontSize: '0.875rem' }}
//                                     >
//                                         Create Event
//                                     </Button>
//                                 </div>
//                                 <Grid container spacing={3} sx={{ padding: 3, }}>
//                                     {events.map(ev => (
//                                         <Grid item xs={12} sm={6} md={4} key={ev.recordId}>
//                                             <EventCard event={ev} />
//                                         </Grid>
//                                     ))}
//                                 </Grid>
//                             </>
//                         )}

//                         {currentView === 'detail' && selectedEvent && (
//                             <Grid container spacing={3}>
//                                 <Grid item xs={12} md={12}>
//                                     <Paper sx={{ p: 2 }}>
//                                         <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
//                                             <Typography variant="h5" fontWeight={700}>
//                                                 {selectedEvent.eventName}
//                                             </Typography>
//                                             <Box display="flex" gap={1} alignItems="center">
//                                                 <Button
//                                                     startIcon={<ArrowBack />}
//                                                     onClick={() => {
//                                                         setCurrentView('dashboard');
//                                                         setSelectedEvent(null);
//                                                     }}
//                                                 >
//                                                     Back
//                                                 </Button>

//                                                 <PayoutButton
//                                                     eventId={selectedEvent?.recordId}
//                                                     disabled={String(selectedEvent?.eventStatus).toUpperCase() === 'COMPLETED'}
//                                                 >
//                                                     {String(selectedEvent?.eventStatus).toUpperCase() === 'COMPLETED'
//                                                         ? 'Payout Already Done'
//                                                         : 'Payout'}
//                                                 </PayoutButton>

//                                             </Box>
//                                         </Box>
//                                         <Divider sx={{ mb: 2 }} />
//                                         <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: '60vh' }}>
//                                             <Table stickyHeader size="small">
//                                                 <TableHead>
//                                                     <TableRow>
//                                                         <TableCell>Rank</TableCell>
//                                                         <TableCell>Inviter</TableCell>
//                                                         <TableCell align="right">Invites</TableCell>
//                                                         <TableCell align="right">Reward Amount</TableCell>
//                                                     </TableRow>
//                                                 </TableHead>
//                                                 <TableBody>
//                                                     {leaderboard.map((inv, idx) => (
//                                                         <LeaderboardRow key={inv.inviterId} inviter={inv} rank={inv.rank || idx + 1} />
//                                                     ))}
//                                                 </TableBody>
//                                             </Table>
//                                         </TableContainer>
//                                     </Paper>
//                                 </Grid>
//                             </Grid>
//                         )}

//                         <EventModal
//                             open={eventModalOpen}
//                             onClose={() => {
//                                 setEventModalOpen(false);
//                                 setEditingEvent(null);
//                             }}
//                             eventData={editingEvent}
//                         />

//                         <ToastContainer position="top-right" autoClose={3000} />
//                     </div>
//                 </Box>
//             </div>
//         </>
//     );
// };

// export default Leaderboard;